import React from 'react';
import { MessageSquare } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useMessages } from '@/hooks/useMessages';
import { formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Chat } from './chat';
import { useFriends } from '@/hooks/useFriends';
import { useAuth } from '@/providers/AuthProvider';
import { db } from '@/lib/firebase';
import { doc, writeBatch } from 'firebase/firestore';

export function MessagesDropdown() {
  const { messages, unreadCount } = useMessages();
  const { friends } = useFriends();
  const { user } = useAuth();
  const [selectedChat, setSelectedChat] = React.useState<string | null>(null);
  const [selectedFriend, setSelectedFriend] = React.useState<any>(null);
  const [isMarkingAllRead, setIsMarkingAllRead] = React.useState(false);

  // Friends from useFriends are already filtered to accepted status
  const acceptedFriends = friends;

  const handleSelectChat = (friend: any, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    const otherUserId =
      (typeof friend?.otherUser?.id === 'string' && friend.otherUser.id) ||
      (Array.isArray(friend?.participants)
        ? friend.participants.find((id: string) => typeof id === 'string' && id !== user?.uid)
        : undefined);

    if (!otherUserId) {
      return;
    }

    setSelectedChat(otherUserId);
    setSelectedFriend(friend);
  };

  const formatMessageDate = (timestamp: any) => {
    if (!timestamp) return 'Just now';
    
    try {
      let date;
      if (timestamp?.toDate) {
        // Handle Firestore Timestamp
        date = timestamp.toDate();
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) {
        return 'Just now';
      }

      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return 'Just now';
    }
  };

  const getLastMessage = (friend: any) => {
    const otherUserId = friend?.otherUser?.id;
    if (!user?.uid || typeof otherUserId !== 'string' || !otherUserId) return undefined;

    return messages.find((message) =>
      message.participants.includes(user.uid) &&
      message.participants.includes(otherUserId)
    );
  };

  const handleMarkAllAsRead = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (!user?.uid || unreadCount <= 0 || isMarkingAllRead) return;

    const unreadMessages = messages.filter(
      (message: any) => message.receiverId === user.uid && !message.seen
    );

    if (unreadMessages.length === 0) return;

    setIsMarkingAllRead(true);

    try {
      const readTimestamp = new Date().toISOString();
      const batchSize = 400;

      for (let index = 0; index < unreadMessages.length; index += batchSize) {
        const chunk = unreadMessages.slice(index, index + batchSize);
        const batch = writeBatch(db);

        chunk.forEach((message: any) => {
          const messageRef = doc(db, 'messages', message.id);
          batch.update(messageRef, {
            seen: true,
            readAt: readTimestamp,
          });
        });

        await batch.commit();
      }
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    } finally {
      setIsMarkingAllRead(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="ghost" 
            size="sm" 
            className="relative p-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-blue-600 dark:text-blue-400"
            aria-label={`Messages${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
          >
            <MessageSquare className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700 shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-slate-700">
            <span className="font-semibold text-slate-700 dark:text-slate-300">Messages</span>
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <>
                  <button
                    type="button"
                    className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-60"
                    onClick={handleMarkAllAsRead}
                    disabled={isMarkingAllRead}
                  >
                    {isMarkingAllRead ? 'Marking...' : 'Mark all read'}
                  </button>
                  <span className="text-sm text-blue-600 dark:text-blue-400">{unreadCount} unread</span>
                </>
              )}
            </div>
          </div>
          
          {acceptedFriends.length === 0 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No friends to message</p>
              <p className="text-xs mt-2">Add some friends to start chatting!</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto">
              {acceptedFriends.map((friend) => {
                const lastMessage = getLastMessage(friend);

                return (
                  <DropdownMenuItem
                    key={friend.id}
                    className="px-4 py-3 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer"
                    onSelect={(e) => e.preventDefault()}
                    onClick={(e) => handleSelectChat(friend, e)}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage 
                          src={friend.otherUser?.photoURL || friend.receiverPhoto || ''} 
                          alt={friend.otherUser?.name || friend.receiverName || 'User'} 
                        />
                        <AvatarFallback className="bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-400">
                          {(friend.otherUser?.name || friend.receiverName || 'U')[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-700 dark:text-slate-300 text-sm">
                          {friend.otherUser?.name || friend.receiverName || 'Unknown User'}
                        </p>
                        {lastMessage ? (
                          <>
                            <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                              {lastMessage.content}
                            </p>
                            <p className="text-xs text-slate-400 dark:text-slate-500">
                              {formatMessageDate(lastMessage.createdAt)}
                            </p>
                          </>
                        ) : (
                          <p className="text-xs text-slate-400 dark:text-slate-500">
                            Start a conversation
                          </p>
                        )}
                      </div>
                      {lastMessage && lastMessage.receiverId === user?.uid && !lastMessage.seen && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></div>
                      )}
                    </div>
                  </DropdownMenuItem>
                );
              })}
            </div>
          )}
          
          <div className="p-3 border-t border-slate-200 dark:border-slate-700">
            <Button
              variant="ghost"
              className="w-full text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => window.location.href = '/messages'}
            >
              View All Messages
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={!!selectedChat} onOpenChange={() => {
        setSelectedChat(null);
        setSelectedFriend(null);
      }}>
        <DialogContent className="sm:max-w-[600px] bg-white dark:bg-slate-900">
          <DialogHeader>
            <DialogTitle className="text-slate-700 dark:text-slate-300">
              {selectedFriend ? `Chat with ${selectedFriend.otherUser?.name || selectedFriend.receiverName}` : 'Messages'}
            </DialogTitle>
          </DialogHeader>
          {selectedChat && <Chat receiverId={selectedChat} />}
        </DialogContent>
      </Dialog>
    </>
  );
}